import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import CountryFlag from "@/app/domain/callflow/timetables/holidaysets/calendar/CountryFlag";
import OverflowDrawer from "@/app/shared/OverflowDrawer";
import { NEW_USER } from "@/constants/New";
import Avatar from "@/design/components/Avatar";
import DeleteFlow from "@/design/components/DeleteFlow";
import HeaderCard from "@/design/components/HeaderCard";
import QRCode from "@/design/components/QRCode";
import SaveIndicator from "@/design/components/SaveIndicator";
import Search from "@/design/components/Search";
import FormGroup from "@/design/forms/FormGroup";
import InputAutocomplete from "@/design/forms/InputAutocomplete";
import InputNumber from "@/design/forms/InputNumber";
import InputNumeric from "@/design/forms/InputNumeric";
import InputPassword from "@/design/forms/InputPassword";
import InputSelect from "@/design/forms/InputSelect";
import InputText from "@/design/forms/InputText";
import InputToggle from "@/design/forms/InputToggle";
import CountryCodes from "@/resources/countrycode";
import { number, string } from "@/utils/hooks/endorse";
import useSave, { SaveState } from "@/utils/hooks/useSave";
import useSearch from "@/utils/hooks/useSearch";
import Sesame from "@/utils/Sesame";
import { formatToMac, toggleLoadingState } from "@/utils/utility";
import Valkyrie, {
  viArrowLeft,
  viCloudSoftphone,
  viFloppyDisk,
  viPassword,
  viPerson,
  viPhone,
  viShieldKeyhole,
  viSliders,
  viSpinnerThird,
  viTrashCan,
  viXmark,
} from "@sippy-platform/valkyrie";

import {
  Alert,
  Box,
  Button,
  Card,
  Container,
  Drawer,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import CodecManager from "./codecs/CodecManager";
import DeviceFeatures from "./DeviceFeatures";
import DistinctiveRingtones from "./distinctiveRingtones/DistinctiveRingtones";
import LineKeyTemplateAccounts from "./lkta/LineKeyTemplateAccounts";
import Personalization from "./Personalization";
import Phonebooks from "./phonebooks/Phonebooks";
import userReducer from "./userReducer";

function User() {
  const { id, pbxId } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  const matches = useMediaQuery(theme.breakpoints.up("md"));

  const pbx = useSelector((state) => state.domain.pbx);
  const phoneNumberList = useSelector((state) => state.domain.pbx.phoneNumbers);
  const inboundRouteStore = useSelector((state) => state.domain.inboundRoutes);
  const musicOnHoldStore = useSelector((state) => state.domain.musicOnHolds);
  const pbxContext = useSelector((state) => state.domain.pbx.context);
  const blocklistStore = useSelector((state) => state.domain.blocklists);
  const distinctiveRingtonesStore = useSelector((state) => state.domain.distinctiveRingtones);

  const [changePassword, setChangePassword] = useState(() => id === "new");
  const [phones, setPhones] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [tab, setTab] = useState(0);
  const [previousId, setPreviousId] = useState(id);

  if (id !== previousId) {
    setPreviousId(id);
    setChangePassword(id === "new");
    setShowPassword(false);
    setTab(0);
  }

  const handleTab = (event, newTab) => setTab(newTab);

  const outgoingNumberList = useMemo(() => {
    return (
      phoneNumberList?.map((phoneNumber) => ({
        did: phoneNumber.did,
        name: inboundRouteStore.list.find((inbound) => inbound.did === phoneNumber.did)?.name || "",
      })) || []
    );
  }, [inboundRouteStore.list, phoneNumberList]);

  // Yealink settings
  const [yealinkSettings, setYealinkSettings] = useState({});
  const formHandlerYealink = (property, value) => {
    setYealinkSettings((prev) => ({ ...prev, [property]: value }));
  };

  const saveYealinkSettings = () => {
    dispatch({ type: "yealinkConfig", value: yealinkSettings });
    setYealinkSettings({});
    setSaveState(SaveState.UNSAVED);
  };

  // Fanvil settings
  const [fanvilSettings, setFanvilSettings] = useState({});
  const formHandlerFanvil = (property, value) => {
    setFanvilSettings((prev) => ({ ...prev, [property]: value }));
  };

  const saveFanvilSettings = () => {
    dispatch({ type: "fanvilConfig", value: fanvilSettings });
    setFanvilSettings({});
    setSaveState(SaveState.UNSAVED);
  };

  // Get general data
  const getPhoneList = useCallback(async () => {
    await Promise.resolve(Api.getProvisionings("unprovisioned")).then((response) => {
      setPhones(response.data.phones);
    });
  }, []);

  useEffect(() => {
    Promise.all([
      Api.getPbx(),
      Api.getMusicOnHolds(),
      Api.getCodecs(),
      Api.getInbounds(),
      Api.getBlocklists(),
      Api.getLineKeyTemplates(),
      Api.getDistinctiveRingtones(),
    ]);
    getPhoneList();
  }, [getPhoneList]);

  const {
    value: user,
    dispatch,
    setSaveState,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: NEW_USER,
    reducer: userReducer,
    validator: () => ({
      name: string()
        .required({ message: "Name is required." })
        .length([1, 40], { message: "Name must be at most 40 characters." })
        .pattern(
          "^[-'a-zA-Z0-9À-ÖØ-öø-ÿ\\ \\,.+_\"–]*$",
          "Name can only contain alphanumeric characters, spaces, commas, dots, and dashes.",
        ),
      exten: number()
        .required({ message: "Number is required." })
        .outside([100, 119], {
          message: "Number cannot be between 100 and 119.",
        })
        .not(0, { message: "Number cannot be 0." })
        .not(1711, { message: "Number cannot be 1711." })
        .not(1712, { message: "Number cannot be 1712." })
        .not(1722, { message: "Number cannot be 1722." })
        .not(1733, { message: "Number cannot be 1733." })
        .not(1813, { message: "Number cannot be 1813." }),
      email: string().email({
        message: "Email address isn't a valid format.",
      }),
      postalcode: string().required({ message: "Postal code is required." }),
      ringSeconds: number()
        .required({ message: "Ring seconds is required." })
        .min(1, { message: "Ring seconds must be between 1 and 120 seconds." })
        .max(120, {
          message: "Ring seconds must be between 1 and 120 seconds.",
        }),
      callLimit: number()
        .required({ message: "Call limit is required." })
        .min(1, { message: "Call limit must be between 1 and 10." })
        .max(10, { message: "Call limit must be between 1 and 10." }),
      sippasswd: string()
        .required({
          message: "Password is required.",
          condition: !user.id || changePassword,
        })
        .length([8], {
          message: "Password must be at least 8 characters long.",
          condition: !user.id || changePassword,
        })
        .pattern("^(?=.*?[a-zA-Z])(?=.*?[0-9]).*$", {
          message: "Password must have at least one letter and one number.",
          condition: !user.id || changePassword,
        })
        .is(user.confirmPassword, {
          message: "Passwords do not match.",
          condition: !user.id || changePassword,
        }),
    }),
    onSubmit: () => Api.storeUser(user),
    onSuccess: (response) => {
      if (!user.id) {
        dispatch({ type: "id", value: response.data.sippeer.id });
        navigate(`../${response.data.sippeer.id}`);

        Promise.resolve(Api.getUser(response.data.sippeer)).then((response) => {
          dispatch({
            type: "distinctiveRingtones",
            value: response.data.sippeer.distinctiveRingtones,
          });
        });
      }

      Promise.resolve(Api.getUsers());
    },
  });

  // Get user data
  useEffect(() => {
    let ignore = false;

    if (Number(id) === user.id || !id) {
      return;
    }

    setSaveState(SaveState.UNCHANGED);

    if (id === "new") {
      Api.cancelActiveGetRequests();

      Promise.resolve(Api.getPbx()).then((response) => {
        const password = new Sesame(16).generate();
        dispatch({
          type: "set",
          value: {
            ...NEW_USER,
            postalcode: response.data.pbx.zip,
            sippasswd: password,
            confirmPassword: password,
            outboundCidNumber: response.data.pbx.defaultPhoneNumber,
          },
        });
      });

      return () => {
        ignore = true;
      };
    }

    toggleLoadingState(true);

    Promise.resolve(Api.getUser({ id: Number(id) }))
      .then((response) => {
        if (!ignore) {
          setYealinkSettings({});
          setFanvilSettings({});
          setChangePassword(false);
          dispatch({ type: "set", value: response.data.sippeer });
        }
      })
      .finally(() => toggleLoadingState(false));

    return () => {
      ignore = true;
    };
  }, [dispatch, id, setSaveState, user.id]);

  // Handle changes
  const formHandler = (property, value) => {
    switch (property) {
      case "exten":
        dispatch({ type: property, value, pbxContext });
        break;
      case "softphone":
        if (value === 1) {
          const newPassword = new Sesame(16).generate();

          setChangePassword(true);
          setShowPassword(true);

          dispatch({ type: property, value, password: newPassword });
        } else {
          dispatch({ type: property, value });
        }
        break;
      default:
        dispatch({ type: property, value });
        break;
    }

    setSaveState(SaveState.UNSAVED);
  };

  // Delete
  function onDeleteFinish() {
    Promise.resolve(Api.getUsers()).then(() => navigate(".."));
  }

  // Page specific functions
  const generatePassword = () => {
    const newPassword = new Sesame(16).generate();

    setChangePassword(true);
    setShowPassword(true);

    dispatch({ type: "resetPassword", value: newPassword });
    setSaveState(SaveState.UNSAVED);
  };

  // Device assignment and drawer
  const [drawer, setDrawer] = useState(null);
  const [devicePending, setDevicePending] = useState(null);
  const [searchValue, setSearchValue] = useState("");

  const searchResults = useSearch(phones, searchValue, [
    "mac",
    "type",
    "phone",
    "sippeer.name",
    "sippeer.exten",
    "flexBot.name",
    "flexUser.name",
    "flexUser.exten",
    "serialNumber",
  ]);

  const closeDrawer = useCallback(() => {
    setDrawer(undefined);
    setSearchValue("");
  }, []);

  const changePhone = async (phone) => {
    setDevicePending(true);
    setDrawer(false);

    if (phone.id) {
      Promise.resolve(Api.putProvisioningUser(phone, user))
        .then(() => {
          dispatch({ type: "device", value: phone });
          getPhoneList();
        })
        .finally(() => setDevicePending(false));
    }
  };

  const deletePhone = async () => {
    setDevicePending(true);

    Promise.resolve(Api.deleteProvisioningUser(user.device, user))
      .then(() => {
        dispatch({ type: "device", value: null });
        getPhoneList();
      })
      .finally(() => setDevicePending(false));
  };

  return (
    <Container>
      <title>{`${user.name || "User"} \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <HeaderCard
          tab={tab}
          changeTab={handleTab}
          tabs={[
            { label: "User", icon: viPerson, id: "user" },
            {
              label: "Device",
              icon: viPhone,
              id: "device",
              disabled: !user.id,
            },
            { label: "Advanced", icon: viSliders, id: "advanced" },
          ]}
          avatar={
            !matches ? (
              <IconButton variant="avatar" component={NavLink} to={".."}>
                <Valkyrie icon={viArrowLeft} />
                <Avatar text={user.name} />
              </IconButton>
            ) : (
              <Avatar text={user.name} />
            )
          }
          action={
            <>
              <SaveIndicator formState={formState} errors={allErrors} save={save} />
              <DeleteFlow
                advanced
                key={user.id || "nk"}
                isActionable={!!user?.id}
                onDeleteCheck={() => Api.deleteUser(user, false)}
                onDelete={() => Api.deleteUser(user, true)}
                name={`${user.name} (${user.exten})`}
                onDeleteFinish={onDeleteFinish}
              />
            </>
          }
          title={user.name || "Unnamed user"}
          subheader={user.exten || "No number set"}
        />
        {tab === 0 && (
          <>
            <FormGroup title="Identity">
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputText required id="name" label="Name" onChange={formHandler} value={user.name} error={errors} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputNumber
                  required
                  id="exten"
                  label="Number"
                  onChange={formHandler}
                  value={user.exten}
                  error={errors}
                  helperText="Number cannot be 0 or any Belgian emergency number."
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputText
                  required
                  id="postalcode"
                  label="Postal Code"
                  onChange={formHandler}
                  value={user.postalcode}
                  error={errors}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputText id="email" label="Email address" onChange={formHandler} value={user.email} error={errors} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputAutocomplete
                  id="distinctiveRingtoneId"
                  label="Distinctive ringtone"
                  onChange={formHandler}
                  value={user.distinctiveRingtoneId}
                  selectValue="id"
                  selects={distinctiveRingtonesStore.list}
                  getOptionLabel={(option) => option.name}
                  hasEmpty={null}
                  error={errors}
                />
              </Grid>
            </FormGroup>
            <FormGroup title="Call flow">
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputAutocomplete
                  id="outboundCidNumber"
                  label="Outgoing number"
                  onChange={formHandler}
                  value={user.outboundCidNumber}
                  selectValue="did"
                  selects={outgoingNumberList}
                  hasEmpty={null}
                  getOptionLabel={(option) => (option.name ? `${option.did} \u00b7 ${option.name}` : option.did)}
                  renderOptionLabel={(option) => (
                    <>
                      {option.did}
                      {option.name !== "" && <span style={{ opacity: 0.6 }}>&nbsp;&middot; {option.name}</span>}
                    </>
                  )}
                  error={errors}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputNumeric
                  required
                  id="ringSeconds"
                  label="Ring seconds"
                  onChange={formHandler}
                  value={user.ringSeconds}
                  min={1}
                  max={120}
                  endAdornment={
                    <InputAdornment position="end" sx={{ marginRight: 1 }}>
                      seconds
                    </InputAdornment>
                  }
                  error={errors}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputSelect
                  id="callRecording"
                  label="Record calls"
                  onChange={formHandler}
                  value={user.callRecording}
                  selectValue="value"
                  selects={[
                    { value: "OFF", title: "Off" },
                    { value: "INCOMING", title: "Only incoming" },
                    { value: "OUTGOING", title: "Only outgoing" },
                    { value: "ALL", title: "On" },
                  ]}
                  getOptionLabel={(option) => option.title}
                  error={errors}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputAutocomplete
                  id="moh"
                  label="Music on hold"
                  onChange={formHandler}
                  value={user.moh}
                  selectValue="name"
                  selects={musicOnHoldStore.list}
                  getOptionLabel={(option) => option.name}
                  error={errors}
                />
              </Grid>
            </FormGroup>
            <FormGroup title="Overflow">
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <OverflowDrawer
                  label="Overflow"
                  value={user.overflow}
                  propertyName="overflow"
                  overflowHandler={(value) => formHandler("overflow", value)}
                  filter={{ exten: [user.exten], custom: [], sound: [] }}
                  helperText="Set the destination if the user does not pick up the call."
                />
              </Grid>
            </FormGroup>
            <FormGroup title="Flex desking">
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputToggle
                  id="flexUser"
                  onChange={formHandler}
                  states={[0, 1]}
                  value={user.flexUser}
                  primary="Flex user"
                  secondary="Allow this users to login through Flex desking."
                  error={errors}
                />
              </Grid>
              {!!user.flexUser && (
                <>
                  <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                    <InputNumber
                      id="flexUserPincode"
                      label="PIN code"
                      onChange={formHandler}
                      value={user.flexUserPincode}
                      error={errors}
                      helperText="An optional code to protect this account from unauthorized logins."
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                    <InputToggle
                      id="askPinOnLogout"
                      onChange={formHandler}
                      states={[false, true]}
                      value={user.askPinOnLogout}
                      primary="Ask PIN code on logout"
                      secondary="Ask the user to enter their PIN code to logout from a device."
                      error={errors}
                    />
                  </Grid>
                </>
              )}
            </FormGroup>
            <FormGroup
              title="Softphone"
              actions={
                !!user.softphone && (
                  <Button
                    component={NavLink}
                    to={`csc:${user.username}@OPTIMISE-GROUP${import.meta.env.SIPPY_ENV === "production" ? "" : "*"}`}
                    startIcon={<Valkyrie icon={viCloudSoftphone} />}
                  >
                    Open CloudSoftphone
                  </Button>
                )
              }
            >
              <Grid size={12}>
                <InputToggle
                  id="softphone"
                  label="Softphone"
                  onChange={formHandler}
                  states={[0, 1]}
                  value={user.softphone}
                  primary="Enable softphone"
                  secondary="Allow this users to login through softphone. This will reset this user's password."
                  error={errors}
                />
              </Grid>
              {!!user.softphone &&
                (user.sippasswd && user.exten && user.confirmPassword === user.sippasswd ? (
                  <Grid size={12}>
                    <QRCode
                      value={`csc:${encodeURIComponent(user.username)}:${encodeURIComponent(user.sippasswd)}@OPTIMISE-GROUP${import.meta.env.SIPPY_ENV === "production" ? "" : "*"}`}
                    />
                    <FormHelperText>
                      This QR code won't be available after leaving this page, store your password or this QR code
                      safely. Changing your password or number later will also invalidate this QR code.
                    </FormHelperText>
                  </Grid>
                ) : (
                  <Grid size={12}>
                    <FormHelperText>
                      {!user.id
                        ? "Make sure the user has the minimum required information set (username, number and password)."
                        : "Fill in the password and confirm it to show the QR code."}
                    </FormHelperText>
                  </Grid>
                ))}
            </FormGroup>
            <FormGroup
              title="Password"
              id="passwordCard"
              actions={
                <Stack
                  direction="row"
                  sx={{
                    gap: 0.5,
                  }}
                >
                  {changePassword ? (
                    <Button
                      variant="contained"
                      onClick={() => setChangePassword(false)}
                      startIcon={<Valkyrie icon={viXmark} />}
                    >
                      Cancel
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={() => setChangePassword(true)}
                      startIcon={<Valkyrie icon={viPassword} />}
                      id="changePassword"
                    >
                      Change password
                    </Button>
                  )}
                </Stack>
              }
            >
              {changePassword && (
                <>
                  <Grid size={12}>
                    <Stack
                      direction="row"
                      sx={{
                        gap: 1,
                      }}
                    >
                      <Button
                        variant="outlined"
                        onClick={() => generatePassword()}
                        startIcon={<Valkyrie icon={viShieldKeyhole} />}
                      >
                        Generate password
                      </Button>
                    </Stack>
                  </Grid>
                  <Grid size={12}>
                    <Typography variant="body2">
                      The new password cannot be seen after you have left this page. The user won't be notified of the
                      password. Make sure to properly and securely communicate the password with the user. A password
                      has to have at least 8 characters with a combination of letters and numbers.
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                    <InputPassword
                      required
                      showPasswordStrength
                      showPassword={showPassword}
                      id="sippasswd"
                      label="Password"
                      onChange={formHandler}
                      value={user.sippasswd}
                      error={errors}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                    <InputPassword
                      required
                      id="confirmPassword"
                      label="Confirm password"
                      onChange={formHandler}
                      value={user.confirmPassword}
                      error={errors}
                    />
                  </Grid>
                </>
              )}
            </FormGroup>
            <CodecManager user={user} setSaveState={setSaveState} dispatch={dispatch} />
          </>
        )}
        {tab === 1 && (
          <>
            <FormGroup title="Device">
              <Grid size={12}>
                {user.device || user.flexDevice ? (
                  <Card variant="outlined">
                    <List>
                      <ListItem
                        disablePadding
                        secondaryAction={
                          <IconButton
                            onClick={() => deletePhone()}
                            disabled={devicePending}
                            aria-label="Delete provisioning device from user"
                          >
                            <Valkyrie icon={devicePending ? viSpinnerThird : viTrashCan} spin={devicePending} />
                          </IconButton>
                        }
                      >
                        <ListItemButton
                          to={`/pbx/${pbxId}/provisioning/${user.device?.mac || user.flexDevice?.mac}`}
                          component={NavLink}
                          sx={{ borderRadius: 0 }}
                        >
                          <ListItemAvatar>
                            <Avatar text={formatToMac(user.device?.mac)} icon={viPhone} />
                          </ListItemAvatar>
                          <ListItemText
                            primary={`${user.exten} \u00b7 ${user.name}${user.flexDevice ? ` \u00b7 Flex Desking` : ""}`}
                            secondary={
                              user.device?.type
                                ? `${user.device?.phone} ${user.device?.type}`
                                : `${user.flexDevice?.phone} ${user.flexDevice?.type}`
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    </List>
                  </Card>
                ) : (
                  <Card variant="outlined">
                    <List>
                      <ListItem disablePadding>
                        <ListItemButton onClick={() => setDrawer(true)} sx={{ borderRadius: 0 }}>
                          <ListItemAvatar>
                            <Avatar text={`Add`} icon={viPhone} />
                          </ListItemAvatar>
                          <ListItemText
                            primary="Add user to a device"
                            secondary="Click to view all available devices"
                          />
                          {devicePending && (
                            <ListItemIcon sx={{ minWidth: 16 }}>
                              <Valkyrie icon={viSpinnerThird} spin />
                            </ListItemIcon>
                          )}
                        </ListItemButton>
                      </ListItem>
                    </List>
                    <Drawer
                      open={!!drawer}
                      onClose={() => closeDrawer()}
                      anchor="right"
                      slotProps={{
                        paper: {
                          sx: (theme) => ({
                            maxWidth: 320,
                            width: 1,
                            p: 1.5,
                            display: "flex",
                            gap: 1,
                            ...theme.scrollbar,
                          }),
                          elevation: 4,
                        },
                      }}
                    >
                      <Stack
                        direction="row"
                        sx={{
                          gap: 1,
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="h5">Assign phone</Typography>
                        <IconButton onClick={() => closeDrawer()} color="inherit">
                          <Valkyrie icon={viXmark} />
                        </IconButton>
                      </Stack>
                      <Box sx={{ position: "sticky", top: 0, zIndex: 1000 }}>
                        <Search blur value={searchValue} onChange={setSearchValue} />
                      </Box>
                      <List>
                        {searchResults.length >= 1 ? (
                          searchResults.map((phone, key) => (
                            <ListItem disablePadding key={key} sx={{ minHeight: 44 }}>
                              <ListItemButton onClick={() => changePhone(phone)} sx={{ minHeight: 44 }}>
                                <ListItemAvatar>
                                  <Avatar text={formatToMac(phone.mac)} icon={viPhone} />
                                </ListItemAvatar>
                                <ListItemText
                                  primary={formatToMac(phone.mac)}
                                  secondary={`${phone.phone} ${phone.type}`}
                                />
                              </ListItemButton>
                            </ListItem>
                          ))
                        ) : (
                          <ListItem>
                            <ListItemText primary="No phones available" />
                          </ListItem>
                        )}
                      </List>
                    </Drawer>
                  </Card>
                )}
              </Grid>
            </FormGroup>
            <DeviceFeatures user={user} formHandler={formHandler} />
            <DistinctiveRingtones user={user} dispatch={dispatch} />
            <Personalization user={user} formHandler={formHandler} />
            <Phonebooks user={user} dispatch={dispatch} formHandler={formHandler} />
            <LineKeyTemplateAccounts user={user} dispatch={dispatch} />
          </>
        )}
        {tab === 2 && (
          <>
            <FormGroup title="Call flow">
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputAutocomplete
                  hasEmpty={null}
                  id="countryCallingCode"
                  label="Country calling code"
                  onChange={formHandler}
                  value={user.countryCallingCode ? `+${user.countryCallingCode}` : null}
                  selectValue="phone"
                  selects={CountryCodes}
                  getOptionLabel={(option) => `${option.phone} \u00b7 ${option.name}`}
                  renderOptionLabel={(option) => (
                    <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
                      <CountryFlag code={option.code} />
                      <span>{`${option.phone} \u00b7 ${option.name}`}</span>
                    </Stack>
                  )}
                  helperText="Select the country calling code for this user. Leave empty to use the default."
                  error={errors}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputNumeric
                  required
                  id="callLimit"
                  label="Call limit"
                  onChange={formHandler}
                  value={user.callLimit}
                  min={1}
                  max={10}
                  error={errors}
                  helperText="The number of calls this user can receive at the same time."
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
                <InputAutocomplete
                  hasEmpty={null}
                  id="blocklistId"
                  label="Blocklist"
                  onChange={formHandler}
                  value={user.blocklistId}
                  selectValue="id"
                  selects={blocklistStore.list.filter((blocklist) => blocklist.type === "user")}
                  getOptionLabel={(option) => option.name}
                  error={errors}
                />
              </Grid>
            </FormGroup>
            <FormGroup title="Sippy Click">
              <Grid size={12}>
                <InputToggle
                  id="enableClickForYealink"
                  onChange={formHandler}
                  states={[0, 1]}
                  value={user.enableClickForYealink}
                  primary="Click for Yealink"
                  secondary="Allow this user to login on Sippy Click for Yealink."
                  error={errors}
                />
              </Grid>
            </FormGroup>
            <FormGroup title="Yealink custom configuration">
              <Grid size={12}>
                <Alert severity="warning">
                  Configuring Yealink features through this interface will overwrite any existing configurations
                  officially supported by Sippy. Note that custom configuration set in user settings will overwrite the
                  configuration set on the PBX level. Only use this at your own risk. Yealink custom configuration won't
                  save automatically to make sure you don't accidentally push an incorrect configuration, which may
                  cause the device to not accept any further changes you make in these settings.
                </Alert>
              </Grid>
              <Grid size={12}>
                <InputText
                  id="yealinkConfig"
                  label="Yealink configuration"
                  onChange={formHandlerYealink}
                  value={yealinkSettings.yealinkConfig ?? user.yealinkConfig}
                  error={errors}
                  multiline
                  rows={8}
                  sx={{
                    "& .MuiInputBase-input": {
                      fontFamily: "monospace",
                    },
                  }}
                />
              </Grid>
              <Grid style={{ display: "flex", justifyContent: "end" }} size={12}>
                <Button
                  variant="contained"
                  onClick={saveYealinkSettings}
                  disabled={Object.keys(yealinkSettings).length === 0}
                  startIcon={<Valkyrie icon={viFloppyDisk} />}
                >
                  Save
                </Button>
              </Grid>
            </FormGroup>
            <FormGroup title="Fanvil custom configuration">
              <Grid size={12}>
                <Alert severity="warning">
                  Configuring Fanvil features through this interface will overwrite any existing configurations
                  officially supported by Sippy. Note that custom configuration set in user settings will overwrite the
                  configuration set on the PBX level. Only use this at your own risk. Fanvil custom configuration won't
                  save automatically to make sure you don't accidentally push an incorrect configuration, which may
                  cause the device to not accept any further changes you make in these settings.
                </Alert>
              </Grid>
              <Grid size={12}>
                <InputText
                  id="fanvilConfig"
                  label="Fanvil configuration"
                  onChange={formHandlerFanvil}
                  value={fanvilSettings.fanvilConfig ?? user.fanvilConfig}
                  error={errors}
                  multiline
                  rows={8}
                  sx={{
                    "& .MuiInputBase-input": {
                      fontFamily: "monospace",
                    },
                  }}
                />
              </Grid>
              <Grid style={{ display: "flex", justifyContent: "end" }} size={12}>
                <Button
                  variant="contained"
                  onClick={saveFanvilSettings}
                  disabled={Object.keys(fanvilSettings).length === 0}
                  startIcon={<Valkyrie icon={viFloppyDisk} />}
                >
                  Save
                </Button>
              </Grid>
            </FormGroup>
          </>
        )}
      </Stack>
    </Container>
  );
}

export default User;
